import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { clientReferencesApi, STORAGE_BASE_URL } from '../../services/adminApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ reference, onEdit, onDelete, onToggleActive }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: reference.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  const statusButtonStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#dc2626',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '0.75rem',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    color: '#dc2626',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'color 0.2s'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  const logoStyle = {
    width: '2rem',
    height: '2rem',
    objectFit: 'contain',
    borderRadius: '0.25rem',
    backgroundColor: '#f3f4f6'
  };

  return (
    <tr 
      ref={setNodeRef} 
      style={style}
    >
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div 
            style={dragHandleStyle} 
            title="Sürüklemek için tut"
            {...attributes}
            {...listeners}
          >
            ⋮⋮
          </div>
          <div style={{ fontWeight: '500' }}>
            {reference.company_name || 'Şirket Adı Yok'}
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {reference.logo ? (
            <img 
              src={`${STORAGE_BASE_URL}${reference.logo}`} 
              alt={reference.company_name}
              style={logoStyle}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div style={{
              ...logoStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
              fontSize: '0.75rem'
            }}>
              🏢
            </div>
          )}
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {reference.logo ? 'Logo var' : 'Logo yok'}
          </span>
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ maxWidth: '15rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {reference.website ? (
            <a 
              href={reference.website} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#2563eb', textDecoration: 'none' }}
            >
              {reference.website}
            </a>
          ) : (
            <span style={{ color: '#6b7280' }}>Website yok</span>
          )}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div>
          {reference.sort_order || 0}
        </div>
      </td>
      <td style={tableCellStyle}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(reference.id, reference.is_active);
          }}
          style={statusButtonStyle(reference.is_active)}
        >
          {reference.is_active ? 'Aktif' : 'Pasif'}
        </button>
      </td>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link
            to={`/admin/client-references/${reference.id}/edit`}
            style={actionLinkStyle}
            onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.color = '#2563eb'}
          >
            Düzenle
          </Link>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(reference.id);
            }}
            style={actionButtonStyle}
            onMouseOver={(e) => e.target.style.color = '#b91c1c'}
            onMouseOut={(e) => e.target.style.color = '#dc2626'}
          >
            Sil
          </button>
        </div>
      </td>
    </tr>
  );
};

const ClientReferencesList = () => {
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchReferences();
  }, []);

  const fetchReferences = async () => {
    try {
      setLoading(true);
      const response = await clientReferencesApi.getAll();
      
      if (response.data.success) {
        const referencesData = response.data.data?.data || response.data.data || [];
        setReferences(referencesData);
        setError(null);
      } else {
        setError('Referanslar yüklenemedi');
      }
    } catch (err) {
      console.error('References fetch error:', err);
      setError('Referanslar yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu referansı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await clientReferencesApi.delete(id);
      if (response.data.success) {
        // Liste yeniden yükle
        await fetchReferences();
        alert('Referans başarıyla silindi');
      } else {
        alert('Referans silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Referans silinirken hata oluştu');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      const response = await clientReferencesApi.update(id, { is_active: !currentStatus });
      if (response.data.success) {
        setReferences(references.map(reference =>
          reference.id === id ? { ...reference, is_active: !currentStatus } : reference
        ));
        alert('Referans durumu güncellendi');
      } else {
        alert('Referans durumu güncellenemedi');
      }
    } catch (err) {
      console.error('Toggle error:', err);
      alert('Referans durumu güncellenirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = references.findIndex(item => item.id === active.id);
      const newIndex = references.findIndex(item => item.id === over.id);
      
      const newReferences = arrayMove(references, oldIndex, newIndex);
      setReferences(newReferences);

      // Update sort_order for all items
      setIsUpdatingOrder(true);
      try {
        const updatePromises = newReferences.map((reference, index) => 
          clientReferencesApi.update(reference.id, { sort_order: index + 1 })
        );
        
        await Promise.all(updatePromises);
        
        // Update local state with new sort_order values
        const updatedReferences = newReferences.map((reference, index) => ({
          ...reference,
          sort_order: index + 1
        }));
        setReferences(updatedReferences);
        
        
      } catch (err) {
        console.error('Sıralama güncellenirken hata:', err);
        alert('Sıralama güncellenirken hata oluştu');
        // Revert on error
        fetchReferences();
      } finally {
        setIsUpdatingOrder(false);
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background-color 0.2s'
  };

  const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  };

  const errorTextStyle = {
    color: '#dc2626',
    marginBottom: '1rem'
  };

  const tableStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Referanslar - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={loadingStyle}>
            <div style={spinnerStyle}></div>
            <p style={{ marginTop: '1rem', color: '#6b7280' }}>Referanslar yükleniyor...</p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Referanslar - Admin Panel</title>
        </Helmet>
        <div style={containerStyle}>
          <div style={errorStyle}>
            <p style={errorTextStyle}>{error}</p>
            <button 
              onClick={fetchReferences}
              style={buttonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Referanslar - Admin Panel</title>
      </Helmet>
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Referanslar ({references.length})</h1>
          <Link
            to="/admin/client-references/create"
            style={buttonStyle}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Yeni Referans Ekle
          </Link>
        </div>

        {references.length === 0 ? (
          <div style={errorStyle}>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>Henüz referans eklenmemiş</p>
            <Link
              to="/admin/client-references/create"
              style={buttonStyle}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
            >
              İlk Referansı Ekle
            </Link>
          </div>
        ) : (
          <div style={tableStyle}>
            <div style={{ overflowX: 'auto' }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={tableHeaderStyle}>Şirket</th>
                      <th style={tableHeaderStyle}>Logo</th>
                      <th style={tableHeaderStyle}>Website</th>
                      <th style={tableHeaderStyle}>Sıralama</th>
                      <th style={tableHeaderStyle}>Durum</th>
                      <th style={tableHeaderStyle}>İşlemler</th>
                    </tr>
                  </thead>
                  <SortableContext
                    items={references.map(r => r.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {references.map((reference, index) => (
                        <SortableItem
                          key={reference.id}
                          reference={reference}
                          onEdit={() => {}}
                          onDelete={handleDelete}
                          onToggleActive={toggleActive}
                        />
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
            </div>
            {isUpdatingOrder && (
              <div style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                zIndex: 1000
              }}>
                Sıralama güncelleniyor...
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ClientReferencesList;
